import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { Spinner } from "@/components/ui/spinner";
import { useClaim } from "@/features/claims/api/use-claim";
import { FileExplorer } from "@/features/file-explorer/components/file-explorer";

const fileExplorerSearchSchema = z.object({
  /** The open folder; the claim's root when absent. */
  folder: z.string().uuid().optional().catch(undefined),
  /** The file open on its own page. */
  file: z.string().uuid().optional().catch(undefined),
});

/**
 * A claim's documents (TECH-1330), full screen over the claims list — which stays mounted underneath, its
 * Algolia state included. Where you are lives in the URL: leaving for an editor and coming back lands in the
 * same folder, and the browser's back button walks the folders.
 */
export const Route = createFileRoute("/_authed/_dashboard/claims/_list/$claimId/files")({
  validateSearch: fileExplorerSearchSchema,
  component: FileExplorerRoute,
});

function FileExplorerRoute() {
  const { claimId } = Route.useParams();
  const { folder, file } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: claim } = useClaim(claimId);

  if (!claim) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
        <Spinner className="size-6" />
      </div>
    );
  }

  return (
    <FileExplorer
      claim={claim}
      folderId={folder ?? null}
      onFolderChange={(folderId) =>
        navigate({ search: (prev) => ({ ...prev, folder: folderId ?? undefined, file: undefined }) })
      }
      fileId={file ?? null}
      // A step of its own in the history: the browser's back button returns to the folder.
      onFileChange={(fileId, folderId) =>
        navigate({
          search: (prev) => ({
            ...prev,
            file: fileId ?? undefined,
            ...(folderId ? { folder: folderId } : {}),
          }),
        })
      }
      // Back to the list as it was: its own params (filters, open claim) are kept, the explorer's dropped.
      onClose={() =>
        navigate({
          to: "/claims",
          search: ({ folder: _folder, file: _file, ...listSearch }) => listSearch,
        })
      }
    />
  );
}
